import { sb } from '../supabase';

// Factory conversations, unified onto the `messages` table (replacing the old
// factory_threads stack). Addressing convention:
//   • buyer → factory :  sender_id = buyer, receiver_id = NULL, factory_id = F
//   • factory owner → buyer : sender_id = owner, receiver_id = buyer, factory_id = F
// The owner resolves the thread via factory_directory.linked_supplier_id (RLS),
// so buyer→factory messages simply wait until the factory registers. No masking.

// Compact product card stored on a message so both sides render it without a join.
// Snapshot taken at send time, stored on messages.product_ref and rendered by
// ProductRefCard. Serves BOTH product systems: factory catalog rows carry
// `image` + `factory_id` + `ref_code`, self-registered supplier rows carry
// `image_url` and none of the other three. A null factory_id is what tells the
// card to link at /products/:id instead of the factory product page.
export function buildProductRef(p) {
  if (!p || !p.id) return null;
  return {
    id: p.id, factory_id: p.factory_id || null,
    name_ar: p.name_ar || null, name_en: p.name_en || null, name_zh: p.name_zh || null,
    image: p.image || p.image_url || null, ref_code: p.ref_code || null,
    price: p.price || null, currency: p.currency || null,
  };
}

// The factory_threads row for a (factory, trader) pair, or null.
//
// factory_threads survives the inbox unification as the factory/request link and
// the owner of share_slug; the messages themselves live in `messages`, tied back
// by thread_id. The pair is unique, so at most one row can match.
//
// Returns null rather than throwing: a conversation that has no thread row is
// still a conversation, and the messages address it by factory_id regardless.
// Nothing here should be able to stop someone sending a message.
async function threadIdFor(factoryId, traderId) {
  if (!factoryId || !traderId) return null;
  const { data } = await sb.from('factory_threads')
    .select('id').eq('factory_id', factoryId).eq('trader_id', traderId).maybeSingle();
  return data?.id || null;
}

// A factory's public identity (buyers can't read base factory_directory).
export async function fetchFactoryIdentity(factoryId) {
  const { data } = await sb.from('factory_directory_public')
    .select('id, company_name, company_name_latin, city, country, profile_image, category')
    .eq('id', factoryId).maybeSingle();
  return data || null;
}

// ── Buyer side ──────────────────────────────────────────────────────────────
// All messages of my conversation with factory F, chronological.
export async function fetchBuyerFactoryMessages(factoryId, myId) {
  const { data, error } = await sb.from('messages')
    .select('*')
    .eq('factory_id', factoryId)
    .or(`sender_id.eq.${myId},receiver_id.eq.${myId}`)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

// Buyer sends to the factory (receiver_id NULL — resolves to the owner on claim).
export async function sendBuyerFactoryMessage(factoryId, myId, content, productRef = null) {
  const body = (content || '').trim();
  if (!body) throw new Error('empty');
  const { data, error } = await sb.from('messages')
    .insert({ sender_id: myId, receiver_id: null, factory_id: factoryId, thread_id: await threadIdFor(factoryId, myId), content: body, product_ref: productRef || null })
    .select('*').single();
  if (error) throw error;
  return data;
}

// The buyer's own factory conversations, grouped by factory, newest first, with
// factory identity, last preview and unread (owner replies the buyer hasn't read).
export async function fetchMyBuyerThreads(myId) {
  const { data, error } = await sb.from('messages')
    .select('id, sender_id, receiver_id, factory_id, content, created_at, is_read')
    .not('factory_id', 'is', null)
    .or(`sender_id.eq.${myId},receiver_id.eq.${myId}`)
    .order('created_at', { ascending: true });
  if (error) throw error;
  const threads = {};
  for (const m of (data || [])) {
    const key = m.factory_id;
    if (!threads[key]) threads[key] = { factory_id: key, last_preview: '', last_at: null, unread: 0 };
    threads[key].last_preview = m.content;   // ascending → last wins
    threads[key].last_at = m.created_at;
    if (m.receiver_id === myId && !m.is_read) threads[key].unread += 1;   // an owner reply to me
  }
  const list = Object.values(threads).sort((a, b) => new Date(b.last_at) - new Date(a.last_at));
  if (!list.length) return [];
  const facIds = list.map((t) => t.factory_id);
  const { data: facs } = await sb.from('factory_directory_public')
    .select('id, company_name, company_name_latin, profile_image').in('id', facIds);
  const fm = Object.fromEntries((facs || []).map((f) => [f.id, f]));
  return list.map((t) => ({ ...t, factory: fm[t.factory_id] || null }));
}

// ── Factory-owner side ──────────────────────────────────────────────────────
// One thread: all messages between factory F and trader T, chronological.
export async function fetchOwnerFactoryMessages(factoryId, traderId) {
  const { data, error } = await sb.from('messages')
    .select('*')
    .eq('factory_id', factoryId)
    .or(`sender_id.eq.${traderId},receiver_id.eq.${traderId}`)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

// The owner's factory conversations, grouped by (factory, trader), newest first,
// with buyer + factory identities, last preview and unread (trader messages the
// owner hasn't read). RLS returns only the owner's factories' rows.
export async function fetchMyOwnerThreads(myId) {
  const { data, error } = await sb.from('messages')
    .select('id, sender_id, receiver_id, factory_id, content, created_at, is_read')
    .not('factory_id', 'is', null)
    .order('created_at', { ascending: true });
  if (error) throw error;
  const threads = {};
  for (const m of (data || [])) {
    // The trader is the non-owner party. A buyer→factory inbound has receiver
    // NULL (trader = sender); an owner reply has sender = me (trader = receiver).
    const trader = m.sender_id === myId ? m.receiver_id : m.sender_id;
    if (!trader) continue;
    const key = `${m.factory_id}:${trader}`;
    if (!threads[key]) threads[key] = { factory_id: m.factory_id, trader_id: trader, last_preview: '', last_at: null, unread: 0 };
    threads[key].last_preview = m.content;   // ascending → last wins
    threads[key].last_at = m.created_at;
    if (m.sender_id !== myId && !m.is_read) threads[key].unread += 1;
  }
  const list = Object.values(threads).sort((a, b) => new Date(b.last_at) - new Date(a.last_at));
  if (!list.length) return [];
  const traderIds = [...new Set(list.map((t) => t.trader_id))];
  const facIds = [...new Set(list.map((t) => t.factory_id))];
  const [{ data: profs }, { data: facs }] = await Promise.all([
    sb.from('profile_directory').select('id, full_name, company_name, avatar_url').in('id', traderIds),
    sb.from('factory_directory_public').select('id, company_name, company_name_latin').in('id', facIds),
  ]);
  const pm = Object.fromEntries((profs || []).map((p) => [p.id, p]));
  const fm = Object.fromEntries((facs || []).map((f) => [f.id, f]));
  return list.map((t) => ({ ...t, trader: pm[t.trader_id] || null, factory: fm[t.factory_id] || null }));
}

// The owner's NEWEST conversation on one specific factory, or null. A factory
// can be talking to several traders, so "open this factory's chat" is ambiguous
// on its own -- the WhatsApp entry routes need this to land on a conversation
// rather than a dashboard. fetchMyOwnerThreads already sorts newest-first, so
// the first match on this factory is the right one.
//
// Still keyed on (factory, trader) rather than thread_id: that pair is what the
// routes carry and what factory_threads is unique on, and it resolves even for
// a conversation that has no thread row.
export async function fetchLatestOwnerThreadForFactory(myId, factoryId) {
  if (!myId || !factoryId) return null;
  const list = await fetchMyOwnerThreads(myId);
  return list.find((t) => t.factory_id === factoryId) || null;
}

// Owner replies to trader T on factory F.
export async function sendOwnerFactoryMessage(factoryId, ownerId, traderId, content) {
  const body = (content || '').trim();
  if (!body) throw new Error('empty');
  const { data, error } = await sb.from('messages')
    .insert({ sender_id: ownerId, receiver_id: traderId, factory_id: factoryId, thread_id: await threadIdFor(factoryId, traderId), content: body })
    .select('*').single();
  if (error) throw error;
  return data;
}
